import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';
import { promotionService } from '@/services/promotion.service';
import type {
  ValidateVoucherPayload,
  VoucherValidationResult,
  EvaluateCartPayload,
  EvaluationResult,
  PromotionCartEvaluationDto,
  PosCustomerTier,
  PosLoyaltyProgram,
  PosLoyaltyTier,
  PromotionListParams,
  VoucherListParams,
  ActiveFlashSale,
} from '@/services/promotion.service';

// ===== PROMOTIONS PROGRAM HOOKS =====
export function usePromotionList(params?: { page?: number; limit?: number; search?: string; status?: string; type?: string; channel?: string; b2b_channel?: string }) {
  return useQuery({
    queryKey: ['promotions', 'list', params],
    queryFn: () => api.get('/promotions/v2', { params }).then((r) => r.data),
    retry: 0,
  });
}

export function useCreatePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any) => api.post('/promotions/v2', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

export function useUpdatePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      api.patch(`/promotions/v2/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

export function useDeletePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/promotions/v2/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

/**
 * TCR-2043 — phạm vi kênh B2B của user trên màn Khuyến mãi (khoá bộ lọc
 * Kênh/ô "Kênh áp dụng", ẩn thao tác ngoài kênh ở menu ⋮). `queryKey` nằm
 * dưới `['promotions']` nên các mutation (create/update/delete/activate/...)
 * `invalidateQueries({ queryKey: ['promotions'] })` sẵn có cũng làm mới scope
 * này — không cần invalidate riêng.
 */
export function usePromotionScope() {
  return useQuery({
    queryKey: ['promotions', 'my-scope'],
    queryFn: () => promotionService.myScope(),
    staleTime: 5 * 60 * 1000,
  });
}

// ===== VOUCHERS HOOKS =====
export function useVoucherList(params?: { page?: number; limit?: number; search?: string; status?: string; voucher_type?: string; channel?: string }) {
  return useQuery({
    queryKey: ['promotions', 'vouchers', params],
    queryFn: () => api.get('/promotions/v2/vouchers', { params }).then((r) => r.data),
    retry: 0,
  });
}

export function useCreateVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any) => api.post('/promotions/v2/vouchers', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

export function useUpdateVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      api.patch(`/promotions/v2/vouchers/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

export function useDeleteVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/promotions/v2/vouchers/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

export function useValidateVoucher() {
  return useMutation({
    mutationFn: (payload: ValidateVoucherPayload): Promise<VoucherValidationResult> =>
      promotionService.validateVoucher(payload).then((r) => r.data),
  });
}

export function useEvaluateCart(payload: EvaluateCartPayload | null) {
  return useQuery({
    queryKey: ['promotions', 'evaluate', payload],
    queryFn: (): Promise<EvaluationResult> =>
      promotionService.evaluate(payload as EvaluateCartPayload).then((r) => r.data),
    enabled: !!payload,
    // review round 3 (finding C): KHÔNG đặt `retry: 0` ở đây — khác các hook
    // list bên trên (`usePromotionList`/`useVoucherList`, người dùng bấm lại
    // được nếu lỗi). Kết quả của query này rơi thẳng vào `promotion_discount`
    // và cộng ngay vào tổng tiền cột phải mà KHÔNG có nút "thử lại" nào — một
    // lần mạng chập chờn zero hoá số giảm và tổng tiền tự nhảy lên, thu ngân
    // không biết. Kế thừa retry mặc định của app (`main.tsx`: retry 1 lần cho
    // lỗi tạm thời, không retry nếu API thật sự down) để một blip đơn lẻ
    // không xoá oan khuyến mãi đang áp dụng.
  });
}

// ===== POS PROMOTION FACADE HOOKS =====
// Keep POS facade DTOs in distinct caches from admin promotion documents.
export function usePosPromotionList(params?: PromotionListParams) {
  return useQuery({
    queryKey: ['pos-promotions', 'list', params],
    queryFn: () => promotionService.pos.listPromotions(params ?? {}),
    retry: 0,
  });
}

export function usePosVoucherList(params?: VoucherListParams) {
  return useQuery({
    queryKey: ['pos-promotions', 'vouchers', params],
    queryFn: () => promotionService.pos.listVouchers(params ?? {}),
    retry: 0,
  });
}

export function useActiveFlashSales(storeId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['pos-promotions', 'flash-sales', 'active', storeId],
    queryFn: (): Promise<ActiveFlashSale[]> =>
      promotionService.pos.getActiveFlashSales(storeId as string),
    enabled: enabled && !!storeId,
    retry: 0,
    // TCR-1143: đủ mới để badge/giá không lệch quá lâu so với remaining_quantity
    // thật, nhưng không refetch liên tục khi tab đang mở — cùng bậc với
    // usePosLoyaltyBalance (30s).
    staleTime: 30_000,
    // Suất flash cạn theo thời gian THỰC, do quầy khác bán mất chứ không phải
    // do thao tác của thu ngân này — không có sự kiện nào ở màn để bám vào.
    // `staleTime` một mình chỉ làm dữ liệu "cũ", không tự đi lấy lại: panel mở
    // suốt ca vẫn hiện số suất của lần fetch đầu tiên. `enabled` đã tắt hẳn
    // query khi panel ẩn hoặc đang ở tab khác, nên nhịp này chỉ chạy đúng lúc
    // thu ngân đang NHÌN vào panel.
    refetchInterval: 30_000,
  });
}

export function usePosValidateVoucher() {
  return useMutation({
    mutationFn: (payload: ValidateVoucherPayload): Promise<VoucherValidationResult> =>
      promotionService.pos.validateVoucher(payload),
  });
}

export function usePosEvaluateCart(payload: EvaluateCartPayload | null) {
  return useQuery({
    queryKey: ['pos-promotions', 'evaluate', payload],
    queryFn: (): Promise<PromotionCartEvaluationDto> =>
      promotionService.pos.evaluateCart(payload as EvaluateCartPayload),
    enabled: !!payload,
  });
}

export function usePosLoyaltyProgram() {
  return useQuery<PosLoyaltyProgram>({
    queryKey: ['pos-promotions', 'loyalty', 'program'],
    queryFn: () => promotionService.pos.getLoyaltyProgram(),
    staleTime: 60_000,
  });
}

export function usePosLoyaltyBalance(customerId?: string) {
  return useQuery<number>({
    queryKey: ['pos-promotions', 'loyalty', 'balance', customerId],
    queryFn: () => promotionService.pos.getLoyaltyBalance(customerId as string),
    enabled: !!customerId,
    staleTime: 30_000,
  });
}

export function usePosLoyaltyTiers() {
  return useQuery<PosLoyaltyTier[]>({
    queryKey: ['pos-promotions', 'loyalty', 'tiers'],
    queryFn: () => promotionService.pos.getLoyaltyTiers(),
    retry: 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePosCustomerTier(customerId?: string) {
  return useQuery<PosCustomerTier>({
    queryKey: ['pos-promotions', 'loyalty', 'customer-tier', customerId],
    queryFn: () => promotionService.pos.getCustomerTier(customerId as string),
    enabled: !!customerId,
    staleTime: 30_000,
  });
}

// ===== FLASH SALES HOOKS =====
export function useFlashSales() {
  return useQuery({
    queryKey: ['promotions', 'flash-sales'],
    queryFn: () => api.get('/promotions/v2/flash-sales').then((r) => r.data?.data ?? []),
    retry: 0,
  });
}

export function useFlashSalesAdmin(params?: { page?: number; limit?: number; search?: string; status?: string; include_deleted?: boolean }) {
  return useQuery({
    queryKey: ['promotions', 'flash-sales', 'admin', params],
    queryFn: () => api.get('/promotions/v2/flash-sales', { params }).then((r) => r.data),
    retry: 0,
  });
}

export function useCreateFlashSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any) => api.post('/promotions/v2/flash-sales', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

export function useUpdateFlashSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      api.patch(`/promotions/v2/flash-sales/${id}`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

export function useDeleteFlashSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/promotions/v2/flash-sales/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

export function useResumeFlashSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => promotionService.resumeFlashSale(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

export function useRestoreFlashSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => promotionService.restoreFlashSale(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['promotions'] }),
  });
}

// TCR-1750 — `enabled` để call site tự tắt query khi user thiếu `loyalty.view`.
// `GET /v1/promotions/v2/loyalty/tiers` được gate bằng key đó ở BE, nên gọi vô
// điều kiện là mọi màn dùng hook này đều nhả 403 vào console cho vai trò không
// có quyền tích điểm (đúng lỗi QA chụp ở màn chi tiết khách hàng). Mặc định
// `true` để mọi call site cũ giữ nguyên hành vi.
export function useLoyaltyTiers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['promotions', 'loyalty-tiers'],
    queryFn: () => promotionService.listTiers().then((r) => r.data ?? r ?? []),
    retry: 0,
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}
